/*
 * The whole job in one shot: a cookbook arrives, the glued spine is trimmed
 * off, the block is punched, and a plastic coil turns its way through the holes
 * until it is bound.
 *
 * Ported from the Next.js build's RebindAnimation + BookArt. Two things carried
 * over deliberately:
 *
 *  1. requestAnimationFrame, not CSS transitions or SMIL. A transition that
 *     fails to tick leaves a half-built book frozen on screen, and this drawing
 *     is the first thing a visitor sees. A rAF loop either runs or does not
 *     exist, and `progress` is a single number that can also just be set.
 *
 *  2. The coil is drawn as a HOOK, not a ring. Seen from directly above, the
 *     wire comes up out of a hole, crosses to the bound edge, wraps around it,
 *     and then runs back UNDER the book where it cannot be seen. Drawing whole
 *     ellipses centred on each hole was the original bug: it put the hidden half
 *     on top of the cover, and the coil read as rings lying on the book rather
 *     than threaded through it. The hook also slants toward the next hole down,
 *     because a coil is one continuous helix, not a stack of rings.
 *
 * The element paints its finished state the moment it is parsed and only then
 * starts animating, so the drawing never depends on the loop running.
 */
(function () {
  'use strict';

  var HOLES = 24;
  var SPINE_X = 150; // outer edge of the glued strip
  var TRIM_X = 178; // the cut line: the glued strip comes off here
  var PUNCH_X = 202; // hole centres, in from the new edge
  var COVER_R = 648;
  var CONTENT_X = 236; // cover artwork starts clear of the coil
  var TOP_Y = 72;
  var BOT_Y = 412;
  var PITCH = (BOT_Y - TOP_Y - 40) / (HOLES - 1);
  var VIEW_BOX = '0 0 720 470';

  var holeYs = [];
  for (var hi = 0; hi < HOLES; hi++) {
    holeYs.push(TOP_Y + 20 + (hi * (BOT_Y - TOP_Y - 40)) / (HOLES - 1));
  }

  var DURATION = 7200; // ms for a full pass
  var ARRIVE = [0, 0.13];
  var CUT = [0.15, 0.36];
  var PUNCH = [0.38, 0.56];
  var COIL = [0.58, 0.94];

  var PHASES = [
    { label: 'Arrives', at: 0.02 },
    { label: 'Spine trimmed', at: 0.25 },
    { label: 'Punched', at: 0.47 },
    { label: 'Coil turned in', at: 0.76 }
  ];

  var DESCRIPTION =
    'A cookbook arrives, its glued spine is trimmed off, the block is punched ' +
    'with a row of holes, and a plastic coil turns through them until the book ' +
    'is bound and lies flat.';

  /* Progress within [a,b], clamped to 0..1. */
  function seg(t, range) {
    return Math.max(0, Math.min(1, (t - range[0]) / (range[1] - range[0])));
  }

  function ease(t) {
    return t * t * (3 - 2 * t);
  }

  function coilPath(y) {
    return (
      'M ' + PUNCH_X + ' ' + y + ' ' +
      'C ' + (PUNCH_X - 13) + ' ' + (y + PITCH * 0.2) + ', ' +
      (TRIM_X - 1) + ' ' + (y + PITCH * 0.26) + ', ' +
      (TRIM_X - 11) + ' ' + (y + PITCH * 0.5) + ' ' +
      'C ' + (TRIM_X - 17) + ' ' + (y + PITCH * 0.72) + ', ' +
      (TRIM_X - 7) + ' ' + (y + PITCH * 0.88) + ', ' +
      (TRIM_X + 2) + ' ' + (y + PITCH * 0.88)
    );
  }

  function markup(uid) {
    var parts = [];

    parts.push(
      '<defs>' +
        '<linearGradient id="cov-' + uid + '" x1="0" y1="0" x2="0.6" y2="1">' +
          '<stop offset="0%" stop-color="#f7f2e7"/><stop offset="100%" stop-color="#e9e0cd"/>' +
        '</linearGradient>' +
        '<linearGradient id="dish-' + uid + '" x1="0" y1="0" x2="0.4" y2="1">' +
          '<stop offset="0%" stop-color="#c08a4e"/><stop offset="100%" stop-color="#7d5327"/>' +
        '</linearGradient>' +
        '<linearGradient id="glue-' + uid + '" x1="0" y1="0" x2="1" y2="0">' +
          '<stop offset="0%" stop-color="#1d1a16"/><stop offset="100%" stop-color="#413a30"/>' +
        '</linearGradient>' +
        '<filter id="sh-' + uid + '" x="-30%" y="-30%" width="160%" height="160%">' +
          '<feGaussianBlur stdDeviation="9"/>' +
        '</filter>' +
        '<filter id="sh2-' + uid + '" x="-40%" y="-40%" width="180%" height="180%">' +
          '<feGaussianBlur stdDeviation="3.5"/>' +
        '</filter>' +
        '<linearGradient id="edge-' + uid + '" x1="0" y1="0" x2="1" y2="0">' +
          '<stop offset="0%" stop-color="#8a7c64" stop-opacity="0.34"/>' +
          '<stop offset="100%" stop-color="#8a7c64" stop-opacity="0"/>' +
        '</linearGradient>' +
        '<linearGradient id="vig-' + uid + '" x1="0" y1="0" x2="0.3" y2="1">' +
          '<stop offset="0%" stop-color="#ffffff" stop-opacity="0.16"/>' +
          '<stop offset="55%" stop-color="#000000" stop-opacity="0"/>' +
          '<stop offset="100%" stop-color="#3a2413" stop-opacity="0.3"/>' +
        '</linearGradient>' +
      '</defs>'
    );

    parts.push('<g data-book>');
    parts.push(
      '<ellipse cx="400" cy="432" rx="250" ry="20" fill="#14202f" opacity="0.16" filter="url(#sh-' + uid + ')"/>'
    );

    /* Page edges peeking past the cover on the three free sides */
    parts.push(
      '<rect x="' + TRIM_X + '" y="' + (TOP_Y + 5) + '" width="' + (COVER_R - TRIM_X + 7) +
      '" height="' + (BOT_Y - TOP_Y) + '" rx="3" fill="#efe7d6"/>'
    );
    parts.push('<g stroke="#dcd0b8" stroke-width="0.8">');
    for (var e = 0; e < 26; e++) {
      var ey = TOP_Y + 12 + e * 12;
      parts.push('<line x1="' + (COVER_R + 1) + '" y1="' + ey + '" x2="' + (COVER_R + 6) + '" y2="' + ey + '"/>');
    }
    parts.push('</g>');

    /* The glued spine — the strip that comes off */
    parts.push('<g data-strip>');
    parts.push(
      '<rect x="' + SPINE_X + '" y="' + TOP_Y + '" width="' + (TRIM_X - SPINE_X) +
      '" height="' + (BOT_Y - TOP_Y) + '" fill="url(#glue-' + uid + ')"/>'
    );
    parts.push('<g stroke="#0f0d0b" stroke-width="0.9" opacity="0.5">');
    for (var s = 0; s < 22; s++) {
      parts.push(
        '<line x1="' + (SPINE_X + 2) + '" y1="' + (TOP_Y + 8 + s * 16) +
        '" x2="' + (TRIM_X - 2) + '" y2="' + (TOP_Y + 2 + s * 16) + '"/>'
      );
    }
    parts.push('</g></g>');

    /* Cover */
    parts.push(
      '<rect x="' + TRIM_X + '" y="' + TOP_Y + '" width="' + (COVER_R - TRIM_X) + '" height="' + (BOT_Y - TOP_Y) +
      '" rx="3" fill="url(#cov-' + uid + ')" stroke="#cdc0a4" stroke-width="1.2"/>'
    );
    /* Paper falls away towards the bound edge, or the cover reads as a flat
       rectangle rather than as a printed board. */
    parts.push(
      '<rect x="' + TRIM_X + '" y="' + TOP_Y + '" width="54" height="' + (BOT_Y - TOP_Y) +
      '" fill="url(#edge-' + uid + ')"/>'
    );
    parts.push(
      '<rect x="' + (CONTENT_X - 4) + '" y="' + (TOP_Y + 20) + '" width="' + (COVER_R - CONTENT_X - 14) +
      '" height="184" rx="3" fill="#fbf7ef"/>'
    );
    parts.push(
      '<rect x="' + CONTENT_X + '" y="' + (TOP_Y + 24) + '" width="' + (COVER_R - CONTENT_X - 22) +
      '" height="176" rx="2" fill="url(#dish-' + uid + ')"/>'
    );
    parts.push(
      '<rect x="' + CONTENT_X + '" y="' + (TOP_Y + 24) + '" width="' + (COVER_R - CONTENT_X - 22) +
      '" height="176" rx="2" fill="url(#vig-' + uid + ')"/>'
    );
    var cx = (CONTENT_X + COVER_R - 22) / 2;
    parts.push('<circle cx="' + cx + '" cy="' + (TOP_Y + 112) + '" r="58" fill="#f4ecdc" opacity="0.94"/>');
    parts.push('<circle cx="' + cx + '" cy="' + (TOP_Y + 112) + '" r="38" fill="#cf9a5c" opacity="0.85"/>');
    parts.push('<circle cx="' + cx + '" cy="' + (TOP_Y + 112) + '" r="19" fill="#8f5f2c" opacity="0.8"/>');
    parts.push(
      '<text x="' + (CONTENT_X + 2) + '" y="' + (TOP_Y + 254) +
      '" font-family="Newsreader, Georgia, serif" font-size="42" fill="#2c2620">Recipes</text>'
    );
    parts.push('<g fill="#8d8070">');
    parts.push('<rect x="' + (CONTENT_X + 3) + '" y="' + (TOP_Y + 272) + '" width="188" height="5" rx="2.5"/>');
    parts.push('<rect x="' + (CONTENT_X + 3) + '" y="' + (TOP_Y + 286) + '" width="132" height="5" rx="2.5"/>');
    parts.push('</g>');
    parts.push('<rect x="' + (CONTENT_X + 3) + '" y="' + (BOT_Y - 46) + '" width="64" height="3" rx="1.5" fill="#a9998a"/>');

    /* Punched holes */
    for (var h = 0; h < HOLES; h++) {
      parts.push(
        '<g data-hole="' + h + '">' +
          '<circle cx="' + PUNCH_X + '" cy="' + holeYs[h] + '" r="4.6" fill="#9d907a"/>' +
          '<circle cx="' + PUNCH_X + '" cy="' + (holeYs[h] + 0.7) + '" r="3.6" fill="#efe7d6"/>' +
        '</g>'
      );
    }

    /* The coil, one hook per hole */
    for (var c = 0; c < HOLES; c++) {
      var d = coilPath(holeYs[c]);
      parts.push(
        '<g data-loop="' + c + '">' +
          '<path d="' + d + '" transform="translate(2.5 3)" fill="none" stroke="#14202f" stroke-opacity="0.3" stroke-width="5" stroke-linecap="round" pathLength="1" filter="url(#sh2-' + uid + ')"/>' +
          '<path d="' + d + '" fill="none" stroke="#141518" stroke-width="4.4" stroke-linecap="round" pathLength="1"/>' +
          '<path d="' + d + '" fill="none" stroke="#31353a" stroke-width="2.9" stroke-linecap="round" pathLength="1"/>' +
          '<path d="' + d + '" fill="none" stroke="#636a73" stroke-width="1.2" stroke-linecap="round" pathLength="1"/>' +
          /* The far rim of the hole, redrawn over the wire so the wire reads as
             coming up THROUGH the paper, not resting on it. */
          '<path d="M ' + (PUNCH_X + 4.4) + ' ' + (holeYs[c] - 2.6) + ' A 4.6 4.6 0 0 1 ' +
            (PUNCH_X + 4.4) + ' ' + (holeYs[c] + 2.6) + '" fill="none" stroke="#9d907a" stroke-width="1.8" stroke-linecap="round"/>' +
        '</g>'
      );
    }

    parts.push('</g>'); // data-book

    /* The blade and the punch head, hidden unless their phase is running */
    parts.push(
      '<g data-blade opacity="0">' +
        '<line data-blade-line stroke="#9c2f2a" stroke-width="2.4"/>' +
        '<polygon data-blade-tip fill="#9c2f2a"/>' +
        '<rect data-blade-body width="30" height="30" rx="5" fill="#f7f4ee" stroke="#9c2f2a" stroke-width="2"/>' +
      '</g>'
    );
    parts.push(
      '<g data-punch opacity="0">' +
        '<rect data-punch-head width="34" height="40" rx="6" fill="#24374f"/>' +
        '<rect data-punch-pin width="10" height="16" rx="2" fill="#24374f"/>' +
      '</g>'
    );

    return parts.join('');
  }

  var counter = 0;

  function setup(host) {
    if (host.dataset.ready === '1') return;
    host.dataset.ready = '1';

    var uid = 'nr' + ++counter;
    var svgNS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('viewBox', VIEW_BOX);
    svg.setAttribute('role', 'img');
    /* aria-label rather than a <title> child: interpolating text into an SVG
       <title> is what broke hydration on the sister site. */
    svg.setAttribute('aria-label', DESCRIPTION);
    svg.setAttribute('class', 'nilya-rebind__svg');
    svg.innerHTML = markup(uid);
    host.insertBefore(svg, host.firstChild);

    var book = svg.querySelector('[data-book]');
    var strip = svg.querySelector('[data-strip]');
    var holes = [];
    var loops = [];
    for (var i = 0; i < HOLES; i++) {
      holes.push(svg.querySelector('[data-hole="' + i + '"]'));
      loops.push(svg.querySelector('[data-loop="' + i + '"]'));
    }
    var loopPaths = loops.map(function (g) {
      return [].slice.call(g.querySelectorAll('path')).slice(0, 4);
    });

    var blade = svg.querySelector('[data-blade]');
    var bladeLine = svg.querySelector('[data-blade-line]');
    var bladeTip = svg.querySelector('[data-blade-tip]');
    var bladeBody = svg.querySelector('[data-blade-body]');
    var punch = svg.querySelector('[data-punch]');
    var punchHead = svg.querySelector('[data-punch-head]');
    var punchPin = svg.querySelector('[data-punch-pin]');

    var phaseEls = [].slice.call(host.querySelectorAll('[data-phase]'));
    var replay = host.querySelector('[data-replay]');
    var replayLabel = host.querySelector('[data-replay-label]');

    function draw(progress) {
      var arrive = ease(seg(progress, ARRIVE));
      var cut = seg(progress, CUT);
      var punchT = seg(progress, PUNCH);
      var coil = seg(progress, COIL);

      /* The book slides in and settles. */
      var bookX = (1 - arrive) * -90;
      book.setAttribute('opacity', String(arrive));
      book.setAttribute('transform', 'translate(' + bookX + ' 0)');

      /* The blade runs down the trim line, then the strip falls away. */
      var stripGone = Math.max(0, (cut - 0.7) / 0.3);
      if (stripGone < 0.8) {
        strip.setAttribute('opacity', String(Math.max(0, 1 - stripGone * 1.25)));
        strip.setAttribute(
          'transform',
          'translate(' + -ease(stripGone) * 230 + ' 0) rotate(' + ease(stripGone) * -14 +
            ' ' + SPINE_X + ' ' + (TOP_Y + BOT_Y) / 2 + ')'
        );
      } else {
        strip.setAttribute('opacity', '0');
      }

      var holesOpen = punchT * HOLES * 1.12;
      for (var i = 0; i < HOLES; i++) {
        var open = Math.max(0, Math.min(1, holesOpen - i));
        var g = holes[i];
        if (open <= 0) {
          g.setAttribute('opacity', '0');
        } else {
          g.setAttribute('opacity', '1');
          g.children[0].setAttribute('r', String(4.6 * open));
          g.children[1].setAttribute('r', String(3.6 * open));
        }
      }

      /* Drawn on rather than faded in: the wire feeds out of the hole and round
         the edge, which is what threading a coil actually looks like. */
      var loopsIn = coil * HOLES * 1.1;
      for (var j = 0; j < HOLES; j++) {
        var seat = Math.max(0, Math.min(1, loopsIn - j));
        if (seat <= 0) {
          loops[j].setAttribute('opacity', '0');
        } else {
          loops[j].setAttribute('opacity', '1');
          var dash = seat + ' ' + (1 - seat + 0.001);
          for (var k = 0; k < loopPaths[j].length; k++) {
            loopPaths[j][k].setAttribute('stroke-dasharray', dash);
          }
        }
      }

      if (cut > 0 && cut < 0.92) {
        var bladeY = TOP_Y - 34 + ease(Math.min(1, cut / 0.7)) * (BOT_Y - TOP_Y + 68);
        var bx = TRIM_X + bookX;
        blade.setAttribute('opacity', String(cut < 0.85 ? 1 : (0.92 - cut) / 0.07));
        bladeLine.setAttribute('x1', String(bx));
        bladeLine.setAttribute('y1', String(TOP_Y - 40));
        bladeLine.setAttribute('x2', String(bx));
        bladeLine.setAttribute('y2', String(bladeY));
        bladeTip.setAttribute(
          'points',
          bx + ',' + (bladeY + 16) + ' ' + (bx - 11) + ',' + (bladeY - 10) + ' ' + (bx + 11) + ',' + (bladeY - 10)
        );
        bladeBody.setAttribute('x', String(bx - 15));
        bladeBody.setAttribute('y', String(bladeY - 40));
      } else {
        blade.setAttribute('opacity', '0');
      }

      if (punchT > 0 && punchT < 0.95) {
        var headY = TOP_Y - 46 + ease(Math.min(1, punchT / 0.85)) * (BOT_Y - TOP_Y + 74);
        var px = PUNCH_X + bookX;
        punch.setAttribute('opacity', String(punchT < 0.88 ? 1 : (0.95 - punchT) / 0.07));
        punchHead.setAttribute('x', String(px - 17));
        punchHead.setAttribute('y', String(headY - 44));
        punchPin.setAttribute('x', String(px - 5));
        punchPin.setAttribute('y', String(headY - 8));
      } else {
        punch.setAttribute('opacity', '0');
      }

      for (var p = 0; p < phaseEls.length; p++) {
        phaseEls[p].classList.toggle('is-done', progress >= PHASES[p].at);
      }
    }

    var frame = 0;
    var started = 0;
    var playing = false;

    function label(progress) {
      var current = PHASES[0].label;
      for (var p = 0; p < PHASES.length; p++) {
        if (progress >= PHASES[p].at) current = PHASES[p].label;
      }
      return current;
    }

    function tick(now) {
      var t = Math.min(1, (now - started) / DURATION);
      draw(t);
      if (replayLabel) replayLabel.textContent = label(t);
      if (t < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        playing = false;
        if (replayLabel) replayLabel.textContent = 'Replay';
      }
    }

    function play() {
      if (playing) cancelAnimationFrame(frame);
      playing = true;
      started = performance.now();
      frame = requestAnimationFrame(tick);
    }

    /* Finished state first, always. Whatever happens to the loop after this,
       the visitor is looking at a bound book and not at half of one. */
    draw(1);
    if (replayLabel) replayLabel.textContent = 'Replay';

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) {
      /* Only once it is actually on screen — autoplaying a 7-second animation
         the visitor has scrolled past is work for nothing. */
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            if (entries[0].isIntersecting) {
              io.disconnect();
              play();
            }
          },
          { threshold: 0.25 }
        );
        io.observe(host);
      } else {
        play();
      }
    }

    if (replay) {
      replay.addEventListener('click', function () {
        play();
      });
    }
  }

  function init() {
    var hosts = document.querySelectorAll('[data-nilya-rebind]');
    for (var i = 0; i < hosts.length; i++) setup(hosts[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor re-renders a section without reloading the page. */
  document.addEventListener('shopify:section:load', init);
})();
